import React, { useState, useEffect } from 'react';
import { useCommitments, usePayouts } from '../hooks/useInvestQueries';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth, api } from '../App';
import AppLayout from '../components/layouts/AppLayout';
import ResponsiveNavbar from '../components/ResponsiveNavbar';
import { TrendingUp, Clock, CheckCircle, DollarSign, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import PaymentInstructionsModal from '../components/ui/PaymentInstructionsModal';

export default function DashboardPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { data: commitments = [] } = useCommitments();
  const { data: payouts = [], isLoading: loading, error } = usePayouts();
  
  // Real-time notifications handled by React Query re-fetches or additional logic if needed
  // For now we rely on the hooks keeping data fresh. 
  // If we want polling: useQuery supports refetchInterval
  
  const [activeTab, setActiveTab] = useState('active');
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedCommitment, setSelectedCommitment] = useState(null);

  if (error) {
    toast.error('Failed to load data');
  }

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      // Even if logout fails, still navigate to login
      navigate('/login');
    }
  };

  const approved = commitments.filter(c => c.status === 'approved' && c.payment_status === 'received');
  const pending = commitments.filter(c => c.status === 'submitted' || c.status === 'approved' && c.payment_status === 'pending');
  const totalInvested = approved.reduce((sum, c) => sum + c.amount, 0);
  const pendingPayouts = payouts.filter(p => p.claim_status !== 'paid');

  return (
    <AppLayout>
      <div className="min-h-screen" data-testid="dashboard-page">
        {/* Responsive Navigation */}
        <ResponsiveNavbar isAdmin={false} />

        {/* Content */}
        <div className="container-responsive max-w-7xl mx-auto py-6 sm:py-8">
          {/* Welcome */}
          <div className="mb-6 sm:mb-8">
            <h1 className="font-['Outfit'] text-2xl sm:text-3xl lg:text-4xl font-bold mb-2">Welcome back, {user?.name}!</h1>
            <p className="text-[#94A3B8] text-sm sm:text-base">Here's an overview of your investment portfolio</p>
          </div>

          {/* Stats - Responsive Grid */}
          <div className="metrics-grid mb-6 sm:mb-8">
          <div className="premium-stat-card p-6" data-testid="stat-total-invested">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[#94A3B8] text-sm uppercase tracking-wider">Total Invested</span>
              <DollarSign className="w-5 h-5 text-[#F5C84C]" />
            </div>
            <p className="font-['JetBrains_Mono'] text-3xl font-bold golden-text">${totalInvested.toLocaleString()}</p>
          </div>
          
          <div className="premium-stat-card p-6" data-testid="stat-active-investments">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[#94A3B8] text-sm uppercase tracking-wider">Active Assets</span>
              <CheckCircle className="w-5 h-5 text-[#FFD76A]" />
            </div>
            <p className="font-['JetBrains_Mono'] text-3xl font-bold golden-text">{approved.length}</p>
          </div>

          <div className="premium-stat-card p-6" data-testid="stat-pending-commitments">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[#94A3B8] text-sm uppercase tracking-wider">Pending</span>
              <Clock className="w-5 h-5 text-[#FFB347]" />
            </div>
            <p className="font-['JetBrains_Mono'] text-3xl font-bold golden-text">{pending.length}</p>
          </div>

          <div className="premium-stat-card p-6" data-testid="stat-pending-payouts">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[#94A3B8] text-sm uppercase tracking-wider">Payouts</span>
              <TrendingUp className="w-5 h-5 text-[#F5C84C]" />
            </div>
            <p className="font-['JetBrains_Mono'] text-3xl font-bold golden-text">{pendingPayouts.length}</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="mb-6 flex items-center gap-4 border-b border-white/10">
          <button onClick={() => setActiveTab('active')} className={`px-4 py-2 font-medium transition-colors ${activeTab === 'active' ? 'text-[#D4AF37] border-b-2 border-[#D4AF37]' : 'text-[#94A3B8] hover:text-white'}`} data-testid="tab-active">Active</button>
          <button onClick={() => setActiveTab('pending')} className={`px-4 py-2 font-medium transition-colors ${activeTab === 'pending' ? 'text-[#D4AF37] border-b-2 border-[#D4AF37]' : 'text-[#94A3B8] hover:text-white'}`} data-testid="tab-pending">Pending</button>
          <button onClick={() => setActiveTab('payouts')} className={`px-4 py-2 font-medium transition-colors ${activeTab === 'payouts' ? 'text-[#D4AF37] border-b-2 border-[#D4AF37]' : 'text-[#94A3B8] hover:text-white'}`} data-testid="tab-payouts">Payouts</button>
        </div>

        {/* Content */}
        {loading ? (
          <div className="text-center py-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D4AF37] mx-auto"></div></div>
        ) : (
          <div>
            {activeTab === 'active' && (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="active-investments-list">
                {approved.length === 0 ? (
                  <div className="col-span-full text-center py-12 text-[#64748B]">No active investments yet. <button onClick={() => navigate('/assets')} className="text-[#D4AF37] hover:underline">Browse assets</button></div>
                ) : (
                  approved.map(commitment => (
                    <div key={commitment.commitment_id} className="premium-card p-6 hover:scale-[1.02] transition-all cursor-pointer" data-testid={`commitment-${commitment.commitment_id}`}>
                      <div className="flex items-start justify-between mb-4">
                        <h3 className="font-['Outfit'] text-xl font-bold">{commitment.asset_name}</h3>
                        <span className="px-2 py-1 bg-[#10B981]/20 text-[#10B981] text-xs rounded-full border border-[#10B981]/30">Active</span>
                      </div>
                      <div className="space-y-2 mb-4">
                        <div className="flex justify-between"><span className="text-[#94A3B8] text-sm">Ownership</span><span className="font-['JetBrains_Mono'] font-semibold">{commitment.percentage}%</span></div>
                        <div className="flex justify-between"><span className="text-[#94A3B8] text-sm">Invested</span><span className="font-['JetBrains_Mono'] font-semibold">${commitment.amount.toLocaleString()}</span></div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}

            {activeTab === 'pending' && (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="pending-commitments-list">
                {pending.length === 0 ? (
                  <div className="col-span-full text-center py-12 text-[#64748B]">No pending commitments</div>
                ) : (
                  pending.map(commitment => (
                    <div key={commitment.commitment_id} className="premium-card p-6" data-testid={`pending-commitment-${commitment.commitment_id}`}>
                      <div className="flex items-start justify-between mb-4">
                        <h3 className="font-['Outfit'] text-xl font-bold">{commitment.asset_name}</h3>
                        <span className="px-2 py-1 bg-[#F59E0B]/20 text-[#F59E0B] text-xs rounded-full border border-[#F59E0B]/30">{commitment.status === 'submitted' ? 'Pending Review' : 'Approved'}</span>
                      </div>
                      <div className="space-y-2">
                        <div className="flex justify-between"><span className="text-[#94A3B8] text-sm">Percentage</span><span className="font-['JetBrains_Mono'] font-semibold">{commitment.percentage}%</span></div>
                        <div className="flex justify-between"><span className="text-[#94A3B8] text-sm">Amount</span><span className="font-['JetBrains_Mono'] font-semibold">${commitment.amount.toLocaleString()}</span></div>
                      </div>
                      
                      {commitment.status === 'approved' && (
                        commitment.payment_status === 'pending' ? (
                          <button 
                            onClick={() => {
                              setSelectedCommitment(commitment);
                              setShowPaymentModal(true);
                            }}
                            className="w-full mt-4 py-2 bg-[#D4AF37]/20 text-[#D4AF37] border border-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#0A1128] font-bold rounded-lg transition-all text-sm flex items-center justify-center gap-2"
                          >
                            View Payment Instructions
                          </button>
                        ) : commitment.payment_status === 'processing' ? (
                          <div className="w-full mt-4 py-2 bg-[#F59E0B]/10 text-[#F59E0B] border border-[#F59E0B]/30 font-bold rounded-lg text-sm flex items-center justify-center gap-2 cursor-default">
                            <Clock className="w-4 h-4" />
                            Payment Processing
                          </div>
                        ) : null
                      )}
                    </div>
                  ))
                )}
              </div>
            )}

            {activeTab === 'payouts' && (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="payouts-list">
                {payouts.length === 0 ? (
                  <div className="col-span-full text-center py-12 text-[#64748B]">No payouts available</div>
                ) : (
                  payouts.map(payout => (
                    <div key={payout.payout_id} className="premium-card p-6" data-testid={`payout-${payout.payout_id}`}>
                      <div className="flex items-start justify-between mb-4">
                        <h3 className="font-['Outfit'] text-xl font-bold">{payout.asset_name}</h3>
                        <span className={`px-2 py-1 text-xs rounded-full border ${
                          payout.claim_status === 'paid' ? 'bg-[#10B981]/20 text-[#10B981] border-[#10B981]/30' :
                          payout.claim_status === 'claim_submitted' ? 'bg-[#F59E0B]/20 text-[#F59E0B] border-[#F59E0B]/30' :
                          'bg-[#3B82F6]/20 text-[#3B82F6] border-[#3B82F6]/30'
                        }`}>{payout.claim_status === 'paid' ? 'Paid' : payout.claim_status === 'claim_submitted' ? 'Processing' : 'Available'}</span>
                      </div>
                      <div className="space-y-2 mb-4">
                        <div className="flex justify-between"><span className="text-[#94A3B8] text-sm">Ownership</span><span className="font-['JetBrains_Mono'] font-semibold">{payout.percentage_owned}%</span></div>
                        <div className="flex justify-between"><span className="text-[#94A3B8] text-sm">Payout</span><span className="font-['JetBrains_Mono'] font-semibold text-[#D4AF37]">${payout.payout_amount.toLocaleString()}</span></div>
                      </div>
                      {payout.claim_status === 'not_claimed' && (
                        <button onClick={async () => {
                          try {
                            await api.post(`/payouts/${payout.payout_id}/claim`);
                            toast.success('Payout claim submitted!');
                            loadData();
                          } catch (error) {
                            toast.error('Failed to claim payout');
                          }
                        }} className="w-full py-2 bg-[#D4AF37] text-[#0A1128] font-bold rounded hover:shadow-[0_0_15px_rgba(212,175,55,0.3)] transition-all" data-testid={`claim-payout-btn-${payout.payout_id}`}>
                          Claim Payout
                        </button>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        )}

        {/* CTA */}
        {approved.length === 0 && activeTab === 'active' && (
          <div className="mt-8 glass-panel p-8 text-center">
            <h3 className="font-['Outfit'] text-2xl font-bold mb-3">Start Investing Today</h3>
            <p className="text-[#94A3B8] mb-6">Browse our exclusive asset opportunities and make your first commitment</p>
            <button onClick={() => navigate('/assets')} className="w-full sm:w-auto px-4 py-2.5 sm:px-6 sm:py-3 lg:px-8 text-sm sm:text-base bg-[#D4AF37] text-[#0A1128] font-bold rounded hover:shadow-[0_0_20px_rgba(212,175,55,0.4)] transition-all" data-testid="browse-assets-cta">Browse Assets</button>
          </div>
        )}
      </div>
      </div>
      <PaymentInstructionsModal 
        isOpen={showPaymentModal} 
        onClose={() => setShowPaymentModal(false)} 
        commitment={selectedCommitment} 
        onPaymentSent={() => {}} // React Query handles cache invalidation if we setup mutation properly, or we can just let stale time handle it. Or invalidate manually.
      />
    </AppLayout>
  );
}
